import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { theme } from '@/constants/theme';
import { buildRuntimePracticeSessionPath, buildRuntimeReviewPath, buildRuntimeReviewRetryPath, type RuntimeBase } from '@/features/unified-learning/runtime/runtimeRoutes';
import { getMistakeAttempt } from '@/features/unified-learning/runtime/mistakeReviewService';
import { requestMistakeAiHelp, type MistakeAiMode } from '@/features/unified-learning/runtime/mistakeAiService';

const BG = '#0B0B14';
const SURFACE = '#16142A';
const BORDER = '#2D2B50';
const TEXT = '#F0EFF8';
const TEXT_MED = '#9896B4';
const PRIMARY = theme.colors.primary;

const QUALITY_LABELS: Record<string, string> = {
  strong: 'מעולה',
  acceptable: 'סביר',
  weak: 'חלש',
  risky: 'מסוכן',
};

const AI_TIMEOUT_MS = 15_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('הבקשה לקחה יותר מדי זמן')), ms)),
  ]);
}

export function RuntimeMistakeDetailScreen({ runtimeBase }: { runtimeBase: RuntimeBase }) {
  const { slug, uid, token, attemptId } = useLocalSearchParams<{ slug: string; uid: string; token?: string; attemptId: string }>();
  const safeSlug     = typeof slug      === 'string' ? slug      : '';
  const safeUid      = typeof uid       === 'string' ? uid       : '';
  const safeToken    = typeof token     === 'string' ? token     : undefined;
  const runtimeSession = { uid: safeUid, token: safeToken };
  const safeAttemptId = typeof attemptId === 'string' ? attemptId : '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof getMistakeAttempt>> | null>(null);
  const [aiLoadingMode, setAiLoadingMode] = useState<MistakeAiMode | null>(null);
  const [aiResult, setAiResult] = useState<any | null>(null);

  const load = useCallback(async () => {
    if (!safeAttemptId) {
      setError('מזהה ניסיון חסר');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getMistakeAttempt(safeAttemptId);
      setDetail(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינת טעות');
    } finally {
      setLoading(false);
    }
  }, [safeAttemptId]);

  useEffect(() => { void load(); }, [load]);

  const anchors = useMemo(() => {
    if (!detail?.scenario?.grounding_map || !Array.isArray(detail.scenario.grounding_map)) return [];
    return detail.scenario.grounding_map
      .map((g: any) => String(g?.anchor ?? '').trim())
      .filter(Boolean)
      .slice(0, 3);
  }, [detail]);

  const runAi = async (mode: MistakeAiMode) => {
    if (!safeSlug || !safeUid || !safeAttemptId) return;
    setAiLoadingMode(mode);
    setAiResult(null);
    try {
      const res = await withTimeout(
        requestMistakeAiHelp({ mode, slug: safeSlug, user_id: safeUid, attempt_id: safeAttemptId }),
        AI_TIMEOUT_MS,
      );
      setAiResult(res);
    } catch {
      setAiResult({
        ok: true,
        fallback: true,
        content: {
          title: 'הסבר מוכן ידנית',
          bullets: [
            'בדוק את חיזוקי התרחיש כדי להבין את העיקרון.',
            'השווה בין ההחלטה שלך לבין התוצאה הרצויה.',
            'בצע ניסיון חוזר עכשיו כדי לקבע את הלמידה.',
          ],
          next_step: 'לחץ "ניסיון חוזר" כדי לבצע תיקון מעשי.',
        },
      });
    } finally {
      setAiLoadingMode(null);
    }
  };

  if (loading) return <View style={s.center}><ActivityIndicator color={PRIMARY} size="large" /></View>;

  if (error || !detail) {
    return (
      <View style={s.center}>
        <Text style={s.error}>{error ?? 'שגיאה בטעינה'}</Text>
        <Pressable style={s.retryBtn} onPress={() => void load()}>
          <Text style={s.retryText}>נסה שוב</Text>
        </Pressable>
        <Pressable style={s.backLinkBtn} onPress={() => router.push(buildRuntimeReviewPath(runtimeBase, safeSlug, runtimeSession) as any)}>
          <Text style={s.backLink}>← חזרה לסקירת טעויות</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <ScrollView style={s.root} contentContainerStyle={s.content}>
      <Text style={s.title}>פרטי טעות</Text>
      <Text style={s.subtitle}>{detail.scenario?.title ?? 'תרחיש'} · {QUALITY_LABELS[detail.attempt.quality_tier] ?? detail.attempt.quality_tier}</Text>

      <View style={s.card}>
        <Text style={s.cardTitle}>מה קרה</Text>
        <Text style={s.body}>{detail.scenario?.situation}</Text>
        <Text style={s.body}>הבחירה שלך: {detail.action?.label}</Text>
      </View>

      <View style={s.card}>
        <Text style={s.cardTitle}>למה זה היה חלש/מסוכן</Text>
        <Text style={s.body}>{detail.consequence?.narrative ?? 'אין תיאור תוצאה זמין.'}</Text>
        {detail.reinforcements.slice(0, 3).map((r, idx) => (
          <View key={`${idx}-${r.headline}`} style={s.reinfRow}>
            <Text style={s.reinfTitle}>{r.headline}</Text>
            <Text style={s.body}>{r.body}</Text>
          </View>
        ))}
      </View>

      <View style={s.card}>
        <Text style={s.cardTitle}>הקשר מקור (אם זמין)</Text>
        {anchors.length === 0
          ? <Text style={s.body}>אין עוגני מקור קשיחים לתרחיש הזה.</Text>
          : anchors.map((a, i) => <Text key={i} style={s.anchor}>"{a}"</Text>)
        }
      </View>

      <View style={s.actionsRow}>
        <Pressable
          style={s.primaryBtn}
          onPress={() => router.push(buildRuntimeReviewRetryPath(runtimeBase, safeSlug, safeAttemptId, runtimeSession) as any)}
        >
          <Text style={s.primaryBtnText}>ניסיון חוזר</Text>
        </Pressable>
        <Pressable
          style={s.secondaryBtn}
          onPress={() => router.push(buildRuntimePracticeSessionPath(runtimeBase, safeSlug, runtimeSession, { mode: 'mistake_detail_practice', lesson_ids: detail.attempt.lesson_id }) as any)}
        >
          <Text style={s.secondaryBtnText}>תרגל אזור חלש זה</Text>
        </Pressable>
      </View>

      <View style={s.card}>
        <Text style={s.cardTitle}>עזרה חכמה ממוקדת</Text>
        <View style={s.aiBtnWrap}>
          <Pressable style={s.aiBtn} onPress={() => { void runAi('simple_explain'); }} disabled={!!aiLoadingMode}>
            <Text style={s.aiBtnText}>הסבר פשוט</Text>
          </Pressable>
          <Pressable style={s.aiBtn} onPress={() => { void runAi('source_grounded'); }} disabled={!!aiLoadingMode}>
            <Text style={s.aiBtnText}>הצג הקשר מקור</Text>
          </Pressable>
          <Pressable style={s.aiBtn} onPress={() => { void runAi('followup_questions'); }} disabled={!!aiLoadingMode}>
            <Text style={s.aiBtnText}>3 שאלות המשך</Text>
          </Pressable>
        </View>
        {aiLoadingMode && <Text style={s.body}>מייצר עזרה ממוקדת…</Text>}
        {aiResult?.content && (
          <View style={s.aiResult}>
            <Text style={s.reinfTitle}>{aiResult.content.title}</Text>
            {(aiResult.content.bullets ?? []).map((b: string, idx: number) => <Text key={idx} style={s.body}>• {b}</Text>)}
            {(aiResult.content.follow_up_questions ?? []).map((q: string, idx: number) => <Text key={`q-${idx}`} style={s.anchor}>? {q}</Text>)}
            {aiResult.content.next_step ? <Text style={s.body}>הלאה: {aiResult.content.next_step}</Text> : null}
          </View>
        )}
      </View>

      <Pressable onPress={() => router.push(buildRuntimeReviewPath(runtimeBase, safeSlug, runtimeSession) as any)}>
        <Text style={s.backLink}>← חזרה לסקירת טעויות</Text>
      </Pressable>
    </ScrollView>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },
  content: { padding: 20, gap: 12, maxWidth: 760, width: '100%', alignSelf: 'center' as any, paddingTop: 40, paddingBottom: 40 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: BG, gap: 12, padding: 32 },
  title: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 28 },
  subtitle: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 13 },
  card: { backgroundColor: SURFACE, borderColor: BORDER, borderWidth: 1, borderRadius: 12, padding: 12, gap: 8 },
  cardTitle: { color: TEXT, fontFamily: theme.fonts.bold, textAlign: 'right' },
  body: { color: TEXT_MED, fontFamily: theme.fonts.regular, textAlign: 'right', lineHeight: 20 },
  reinfRow: { borderTopWidth: 1, borderTopColor: BORDER, paddingTop: 8 },
  reinfTitle: { color: TEXT, fontFamily: theme.fonts.semiBold, textAlign: 'right' },
  anchor: { color: '#C4B5FD', fontFamily: theme.fonts.regular, textAlign: 'right', fontSize: 12 },
  actionsRow: { gap: 8 },
  primaryBtn: { backgroundColor: PRIMARY, borderRadius: 10, alignItems: 'center', paddingVertical: 11 },
  primaryBtnText: { color: '#fff', fontFamily: theme.fonts.bold },
  secondaryBtn: { borderWidth: 1, borderColor: PRIMARY, borderRadius: 10, alignItems: 'center', paddingVertical: 11 },
  secondaryBtnText: { color: PRIMARY, fontFamily: theme.fonts.bold },
  aiBtnWrap: { flexDirection: 'row-reverse', gap: 8, flexWrap: 'wrap' },
  aiBtn: { backgroundColor: '#221F3D', borderRadius: 10, borderWidth: 1, borderColor: BORDER, paddingVertical: 8, paddingHorizontal: 10 },
  aiBtnText: { color: TEXT, fontFamily: theme.fonts.semiBold, fontSize: 12 },
  aiResult: { borderTopWidth: 1, borderTopColor: BORDER, paddingTop: 8, gap: 3 },
  backLink: { color: PRIMARY, fontFamily: theme.fonts.bold, textAlign: 'center', marginTop: 8 },
  backLinkBtn: { marginTop: 8 },
  error: { color: '#FCA5A5', fontFamily: theme.fonts.bold, textAlign: 'center' },
  retryBtn: { backgroundColor: PRIMARY, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 10 },
  retryText: { color: '#fff', fontFamily: theme.fonts.bold },
});
